import * as React from 'react';
import { useEffect, useRef } from 'react';
import * as TabsPrimitive from '@radix-ui/react-tabs';

import { cn } from '@/lib/utils';

function Tabs({
  className,
  ...props
}: React.ComponentProps<typeof TabsPrimitive.Root>) {
  return (
    <TabsPrimitive.Root
      data-slot="tabs"
      className={cn('flex flex-col gap-2', className)}
      {...props}
    />
  );
}

const INDICATOR_STORAGE_KEY = 'tabs-indicator-position';

function TabsList({
  className,
  ...props
}: React.ComponentProps<typeof TabsPrimitive.List>) {
  const listRef = useRef<HTMLDivElement>(null);
  const indicatorRef = useRef<HTMLDivElement>(null);
  const hasAnimated = useRef(false);

  useEffect(() => {
    const list = listRef.current;
    const indicator = indicatorRef.current;
    if (!list || !indicator) return;

    const activeTab = list.querySelector<HTMLElement>('[data-state="active"]');
    if (!activeTab) {
      indicator.style.opacity = '0';
      return;
    }

    // offsetLeft / offsetTop are measured from the offsetParent's padding box
    // (the listRef, since it's position:relative). This already accounts for
    // the list's border + padding, so the indicator aligns to the tab's
    // outer box without any magic offset.
    const targetX = activeTab.offsetLeft;
    const targetY = activeTab.offsetTop;
    const targetWidth = activeTab.offsetWidth;
    const targetHeight = activeTab.offsetHeight;

    if (!hasAnimated.current) {
      // On first mount, check if there's a saved previous position to animate from
      const saved = sessionStorage.getItem(INDICATOR_STORAGE_KEY);
      if (saved) {
        const prev = JSON.parse(saved);
        // Start at previous position (no transition)
        indicator.style.transition = 'none';
        indicator.style.opacity = '1';
        indicator.style.top = `${targetY}px`;
        indicator.style.width = `${prev.width}px`;
        indicator.style.height = `${targetHeight}px`;
        indicator.style.transform = `translateX(${prev.x}px)`;

        // Force reflow then animate to new position
        indicator.getBoundingClientRect();
        indicator.style.transition =
          'transform 320ms cubic-bezier(0.16, 1, 0.3, 1), width 320ms cubic-bezier(0.16, 1, 0.3, 1)';
        indicator.style.width = `${targetWidth}px`;
        indicator.style.transform = `translateX(${targetX}px)`;
      } else {
        // No previous position — just show immediately
        indicator.style.transition = 'none';
        indicator.style.opacity = '1';
        indicator.style.top = `${targetY}px`;
        indicator.style.width = `${targetWidth}px`;
        indicator.style.height = `${targetHeight}px`;
        indicator.style.transform = `translateX(${targetX}px)`;
      }
      hasAnimated.current = true;
    } else {
      indicator.style.transition =
        'transform 320ms cubic-bezier(0.16, 1, 0.3, 1), width 320ms cubic-bezier(0.16, 1, 0.3, 1)';
      indicator.style.opacity = '1';
      indicator.style.top = `${targetY}px`;
      indicator.style.width = `${targetWidth}px`;
      indicator.style.height = `${targetHeight}px`;
      indicator.style.transform = `translateX(${targetX}px)`;
    }

    // Save current position for cross-navigation animation
    sessionStorage.setItem(
      INDICATOR_STORAGE_KEY,
      JSON.stringify({ x: targetX, width: targetWidth })
    );
  });

  return (
    <TabsPrimitive.List
      ref={listRef}
      data-slot="tabs-list"
      className={cn(
        'bg-background border border-border text-muted-foreground relative inline-flex h-9 w-fit items-center justify-center rounded-lg p-[4px]',
        className
      )}
      {...props}
    >
      <div
        ref={indicatorRef}
        className="absolute left-0 top-0 rounded-sm border border-border bg-secondary shadow-premium"
        style={{ opacity: 0, pointerEvents: 'none' }}
      />
      {props.children}
    </TabsPrimitive.List>
  );
}

function TabsTrigger({
  className,
  ...props
}: React.ComponentProps<typeof TabsPrimitive.Trigger>) {
  return (
    <TabsPrimitive.Trigger
      data-slot="tabs-trigger"
      className={cn(
        "relative z-10 data-[state=active]:text-foreground focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:outline-ring text-muted-foreground inline-flex h-full flex-1 items-center justify-center gap-1.5 rounded-md border border-transparent text-sm font-medium whitespace-nowrap transition-colors duration-200 focus-visible:ring-[3px] focus-visible:outline-1 disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-4",
        className
      )}
      {...props}
    />
  );
}

function TabsContent({
  className,
  ...props
}: React.ComponentProps<typeof TabsPrimitive.Content>) {
  return (
    <TabsPrimitive.Content
      data-slot="tabs-content"
      className={cn('flex-1 outline-none', className)}
      {...props}
    />
  );
}

export { Tabs, TabsList, TabsTrigger, TabsContent };
